import React from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

type Testimonial = {
  id: string;
  body: string;
  author: string;
  meta?: string;
};

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default function TestimonialsCarousel() {
  const { t, i18n } = useTranslation();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
  const [items, setItems] = React.useState<Testimonial[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/public/testimonials`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          items?: Array<{ id: string; quote: string; authorName: string; authorMeta?: string | null }>;
        };
        const next =
          (data.items || [])
            .map((x) => ({
              id: String(x.id),
              body: String(x.quote || '').trim(),
              author: String(x.authorName || '').trim(),
              meta: x.authorMeta ? String(x.authorMeta).trim() : undefined,
            }))
            .filter((x) => x.body && x.author) || [];
        if (!cancelled) setItems(next);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, i18n.language, i18n.resolvedLanguage]);

  const [perPage, setPerPage] = React.useState(3);
  const pages = React.useMemo(() => chunk(items, perPage), [items, perPage]);
  const [pageIdx, setPageIdx] = React.useState(0);

  React.useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w < 640) return 1;
      if (w < 1024) return 2;
      return 3;
    };
    const apply = () => setPerPage(calc());
    apply();
    window.addEventListener('resize', apply, { passive: true });
    return () => window.removeEventListener('resize', apply as any);
  }, []);

  React.useEffect(() => {
    // Ensure pageIdx stays valid when perPage changes.
    setPageIdx((p) => Math.min(p, Math.max(0, pages.length - 1)));
  }, [pages.length]);

  React.useEffect(() => {
    if (pages.length <= 1) return;
    const id = window.setInterval(() => {
      setPageIdx((p) => (p + 1) % pages.length);
    }, 7000);
    return () => window.clearInterval(id);
  }, [pages.length]);

  if (!items.length) return null;
  const active = pages[pageIdx] || pages[0] || [];

  const prev = () => setPageIdx((p) => (p - 1 + pages.length) % pages.length);
  const next = () => setPageIdx((p) => (p + 1) % pages.length);

  return (
    <section
      className="border-t border-slate-200/60 py-14 sm:py-20"
      style={{ backgroundColor: 'var(--storefront-body-bg, #f1f5f9)' }}
    >
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('storefront.testimonials_title')}
          </h2>
          <p className="mt-2 text-sm text-slate-600 sm:text-base">
            {t('storefront.testimonials_subtitle')}
          </p>
        </div>

        <div className="relative">
          {pages.length > 1 ? (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute -left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 md:inline-flex"
                aria-label={t('storefront.testimonials_prev')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute -right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 md:inline-flex"
                aria-label={t('storefront.testimonials_next')}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {active.map((it, idx) => (
              <article
                key={it.id || `${pageIdx}:${idx}`}
                className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 p-5 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#00b853]/10 text-[#00b853]">
                    <Quote className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-relaxed text-slate-700">
                      “{it.body}”
                    </p>
                    <p className="mt-4 text-sm font-semibold text-slate-900">{it.author}</p>
                    {it.meta ? (
                      <p className="text-xs text-slate-500">{it.meta}</p>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {pages.length > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-2">
              {pages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPageIdx(i)}
                  className={[
                    'h-2.5 w-2.5 rounded-full border transition-colors',
                    i === pageIdx
                      ? 'border-[#00b853] bg-[#00b853]'
                      : 'border-slate-300 bg-white hover:border-slate-400',
                  ].join(' ')}
                  aria-label={t('storefront.testimonials_go_to', { n: i + 1 })}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

